import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";
import AiDescriptionButtons from "../components/AiDescriptionButtons";

/**
 * <BusinessCompleteProfile /> — post-Google business setup form.
 *
 * Google gives us email + full_name. Businesses still need: business_name,
 * business_description, contact_phone, contact_email (pre-filled), and DOB.
 * We block them out of the business dashboard until this is complete
 * (routing in AuthCallback handles the redirect the first time).
 */
export default function BusinessCompleteProfile() {
  const { user, authHeader, refreshUser, loading } = useAuth();
  const nav = useNavigate();
  const [businessName, setBusinessName] = useState("");
  const [description, setDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [dob, setDob] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    if (user.role !== "business") { nav("/", { replace: true }); return; }
    if (user.business_name && user.business_description && user.contact_phone && user.date_of_birth) {
      nav("/business/dashboard", { replace: true });
      return;
    }
    // Prefill contact email from Google if we have it
    if (user.email && !contactEmail) setContactEmail(user.email);
    if (user.contact_email && !contactEmail) setContactEmail(user.contact_email);
  }, [loading, user?.id, user?.business_name, user?.contact_phone, user?.date_of_birth, user?.role, nav]);

  if (loading || !user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!businessName.trim() || !description.trim() || !phone.trim() || !dob) {
      toast.error("Please fill in every field");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/google/complete/business`, {
        business_name: businessName.trim(),
        business_description: description.trim(),
        contact_phone: phone.trim(),
        contact_email: contactEmail.trim() || undefined,
        date_of_birth: dob,
      }, { headers: authHeader() });
      await refreshUser();
      toast.success("Business profile saved");
      nav("/business/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your details");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App" data-testid="business-complete-profile-page">
      <Header hideSearch />
      <div className="max-w-lg mx-auto px-6 py-14">
        <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Finish your business setup</div>
        <h1 className="font-display text-3xl mt-2" style={{ color: "var(--gf-text)" }}>Tell us about your business</h1>
        <p className="text-sm mt-2" style={{ color: "var(--gf-text-muted)" }}>Google gave us your name and email — we need a few more details before your listings can go live.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field label="Business name" required>
            <input data-testid="biz-name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} required
                   className={cls()} style={inputStyle()} />
          </Field>
          <Field label="Business description" required hint="What do you offer? (10+ characters)">
            <textarea data-testid="biz-description" value={description} onChange={(e) => setDescription(e.target.value)} required rows={3}
                      className={cls()} style={inputStyle()} />
            <AiDescriptionButtons value={description} onChange={setDescription} testidPrefix="complete-ai-desc" />
          </Field>
          <Field label="Contact phone" required>
            <input data-testid="biz-phone" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required
                   placeholder="+15551234567" className={cls()} style={inputStyle()} />
          </Field>
          <Field label="Contact email" hint="We prefilled this from Google — change it if you want a different inbox for booking notifications.">
            <input data-testid="biz-contact-email" type="email" autoComplete="email" value={contactEmail}
                   onChange={(e) => setContactEmail(e.target.value)} className={cls()} style={inputStyle()} />
          </Field>
          <Field label="Your date of birth" required hint="Required for account verification — you must be at least 18.">
            <input data-testid="biz-dob" type="date" autoComplete="bday" value={dob} onChange={(e) => setDob(e.target.value)} required
                   className={cls()} style={inputStyle()} />
          </Field>

          <button type="submit" data-testid="business-complete-submit" disabled={submitting}
                  className="w-full rounded-full py-2.5 font-medium disabled:opacity-50"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
            {submitting ? "Saving…" : "Complete signup"}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block text-sm" style={{ color: "var(--gf-text-soft)" }}>
      <span>{label}{required ? " *" : ""}</span>
      {children}
      {hint && <span className="block text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>{hint}</span>}
    </label>
  );
}
function cls() { return "mt-1 w-full bg-transparent border rounded-md px-3 py-2 outline-none focus:border-[var(--gf-accent)]"; }
function inputStyle() { return { borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }; }
